/**
 * A plain month grid for MSR availability (business lead, 2026-10-27): days the rep can work are GREEN.
 * In edit mode a tap toggles a day; with "Select a range" on, the first tap starts a range and the second tap fills
 * every day between (inclusive). Days before `minDay` cannot be changed. Optional blue dots mark days the rep is
 * scheduled on an event; optional `cellExtra` renders more inside a cell (the manager's team view lists names).
 */
import React from "react";
import {
  datesBetween,
  monthEnd,
  monthStart,
} from "../../services/msrFeedback.service";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export interface AvailabilityMonthCalendarProps {
  month: string; // any date in the month, YYYY-MM-DD
  available: Set<string>;
  editable?: boolean;
  minDay?: string; // days before this are locked (YYYY-MM-DD)
  today?: string; // ringed and labelled "Today" so a rep can find it (bug 2026-10-30)
  scheduled?: Set<string>;
  rangeStart?: string | null; // first tap of a range, highlighted
  onDayClick?: (day: string) => void;
  cellExtra?: (day: string) => React.ReactNode;
  compact?: boolean;
}

export function monthGridDays(month: string): (string | null)[] {
  const first = monthStart(month);
  const days = datesBetween(first, monthEnd(first));
  const lead = new Date(`${first}T12:00:00Z`).getUTCDay();
  const cells: (string | null)[] = [...Array(lead).fill(null), ...days];
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  return cells;
}

const AvailabilityMonthCalendar: React.FC<AvailabilityMonthCalendarProps> = ({
  month,
  available,
  editable = false,
  minDay,
  today,
  scheduled,
  rangeStart,
  onDayClick,
  cellExtra,
  compact = false,
}) => {
  const cells = monthGridDays(month);
  return (
    <div data-testid="availability-month-calendar">
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-gray-500">
        {WEEKDAYS.map((w) => (
          <div key={w}>{w}</div>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (!day) {
            return <div key={`blank-${i}`} />;
          }
          const on = available.has(day);
          const locked = !!minDay && day < minDay;
          const isStart = rangeStart === day;
          const isToday = !!today && day === today;
          let ring = "";
          if (isStart) {
            ring = "ring-2 ring-blue-500 ring-offset-1";
          } else if (isToday) {
            ring = "ring-2 ring-gray-900 ring-offset-1";
          }
          const clickable = editable && !locked && !!onDayClick;
          const base = compact ? "min-h-[44px]" : "min-h-[52px]";
          const color = on
            ? "bg-green-500 text-white border-green-600"
            : locked
              ? "bg-gray-50 text-gray-300 border-gray-100"
              : "bg-white text-gray-800 border-gray-200";
          const content = (
            <>
              <span className="text-sm font-semibold leading-none">
                {Number(day.slice(8, 10))}
              </span>
              {isToday && (
                <span className="mt-0.5 text-[9px] font-bold uppercase leading-none tracking-wide">
                  Today
                </span>
              )}
              {scheduled?.has(day) && (
                <span
                  className={`mt-1 inline-block h-1.5 w-1.5 rounded-full ${on ? "bg-white" : "bg-blue-600"}`}
                  aria-label="scheduled"
                />
              )}
              {cellExtra?.(day)}
            </>
          );
          return clickable ? (
            <button
              key={day}
              type="button"
              onClick={() => onDayClick?.(day)}
              aria-pressed={on}
              aria-label={`${day}${isToday ? ", today" : ""}${on ? ", available" : ""}${scheduled?.has(day) ? ", scheduled" : ""}`}
              aria-current={isToday ? "date" : undefined}
              data-day={day}
              data-today={isToday ? "true" : undefined}
              className={`${base} flex flex-col items-center justify-start rounded-md border p-1 ${color} ${ring} hover:opacity-90`}
            >
              {content}
            </button>
          ) : (
            <div
              key={day}
              data-day={day}
              data-available={on ? "true" : "false"}
              data-today={isToday ? "true" : undefined}
              aria-current={isToday ? "date" : undefined}
              className={`${base} flex flex-col items-center justify-start rounded-md border p-1 ${color} ${ring}`}
            >
              {content}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AvailabilityMonthCalendar;
